/** Order-level POSTAGE rows (one shipment fee per order, not per voucher line). */

export function sumOrderPostageDiscounts(
  discounts: Array<{ discountType?: string; discountValue?: number | null }> | undefined | null,
): number {
  return (discounts ?? [])
    .filter((d) => d.discountType === 'POSTAGE')
    .reduce((sum, d) => sum + (d.discountValue ?? 0), 0)
}

export type VoucherCartPostageInput = {
  id: string
  deliveryMethod?: string | null
  orderId?: string | null
  createdAt?: string | null
  orderPostage?: number
}

/** Assign the full order postage to the first physical voucher only (by createdAt). */
export function buildVoucherCartPostageFeeMap(
  vouchers: VoucherCartPostageInput[],
): Map<string, number> {
  const fees = new Map<string, number>()
  const assignedOrders = new Set<string>()

  const sorted = [...vouchers].sort((a, b) =>
    String(a.createdAt ?? '').localeCompare(String(b.createdAt ?? '')),
  )

  for (const voucher of sorted) {
    if ((voucher.deliveryMethod ?? '').toUpperCase() !== 'PHYSICAL') continue
    if (!voucher.orderId || assignedOrders.has(voucher.orderId)) continue

    const postage = voucher.orderPostage ?? 0
    if (postage > 0) {
      fees.set(voucher.id, postage)
      assignedOrders.add(voucher.orderId)
    }
  }

  return fees
}

export function computeCartTotals(items: Array<{ price: number; postageFee?: number | null }>) {
  const subtotal = items.reduce((sum, item) => sum + item.price, 0)
  const postageTotal = items.reduce((sum, item) => sum + (item.postageFee ?? 0), 0)
  return { subtotal, postageTotal, total: subtotal + postageTotal }
}

/** Resolve the order's postage option from an existing cart (one shipment fee per order). */
export function findSharedOrderPostageOptionIdFromCart(
  items: Array<{
    type?: string
    deliveryMethod?: string | null
    orderPostageOptionName?: string | null
    orderPostageFee?: number | null
  }>,
  postageOptions: Array<{ id: string; name: string; fee: number }>,
): string | undefined {
  for (const item of items) {
    if (item.type !== 'VOUCHER') continue
    const delivery = (item.deliveryMethod ?? '').toUpperCase()
    if (delivery !== 'PHYSICAL' && delivery !== 'POST') continue
    if (!item.orderPostageOptionName && item.orderPostageFee == null) continue

    if (item.orderPostageOptionName) {
      const byName = postageOptions.find(
        (option) => option.name.trim() === item.orderPostageOptionName?.trim(),
      )
      if (byName) return byName.id
    }

    if (item.orderPostageFee != null) {
      const byFeeMatches = postageOptions.filter((option) => option.fee === item.orderPostageFee)
      if (byFeeMatches.length === 1) return byFeeMatches[0].id
    }
  }

  return undefined
}
